import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SearchIcon, ReceiptIcon, FilterIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { InvoiceCard, Invoice } from '../components/InvoiceCard';
import { AddInvoiceModal } from '../components/AddInvoiceModal';
import { EditInvoiceModal } from '../components/EditInvoiceModal';
interface InvoicesProps {
  invoices: Invoice[];
  onAddInvoice: (invoice: Omit<Invoice, 'id'>) => void;
  onEditInvoice: (invoice: Invoice) => void;
  onDeleteInvoice: (invoice: Invoice) => void;
  canAddInvoice: boolean;
  customers: Array<{
    id: string;
    customerId: string;
    name: string;
  }>;
}
export function Invoices({
  invoices,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
  canAddInvoice,
  customers
}: InvoicesProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [paidFilter, setPaidFilter] = useState<string>('all');
  const isReadOnly = !canAddInvoice;
  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const matchesSearch = invoice.supplier.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.customerName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || invoice.category === categoryFilter;
      const matchesPaid = paidFilter === 'all' || paidFilter === 'paid' && invoice.paid || paidFilter === 'unpaid' && !invoice.paid;
      return matchesSearch && matchesCategory && matchesPaid;
    });
  }, [invoices, searchQuery, categoryFilter, paidFilter]);
  // Calculate stats
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = invoices.filter(inv => inv.paid).reduce((sum, inv) => sum + inv.amount, 0);
  const unpaidAmount = totalAmount - paidAmount;
  return <div className="space-y-6" data-id="element-1529" data-mp="3om2xf7zynx8k">
      {/* Header */}
      <header className="flex items-center justify-between" data-id="element-1530" data-mp="3jmghgqsr02w2">
        <div data-id="element-1531" data-mp="3jkvmx16fojy7">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1532" data-mp="3joji04rj1ef1">Fakturaer</h1>
          <p className="text-stone-600" data-id="element-1533" data-mp="2c8o0g1czsz2">
            {isReadOnly ? 'Se dine indkøbsfakturaer' : 'Administrer dine indkøbsfakturaer'}
          </p>
        </div>
        {canAddInvoice && <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2" data-id="element-1534" data-mp="b87vi1kqeacv">
            <PlusIcon className="w-5 h-5" data-id="element-1535" data-mp="l41fg60s564o" />
            Tilføj faktura
          </Button>}
      </header>

      {/* Stats */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1536" data-mp="g6strvgfajyo">
        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1537" data-mp="ptvevkj41auo">
          <div className="flex items-center gap-3 mb-2" data-id="element-1538" data-mp="urnjnf4usrga">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1539" data-mp="uu9qtdnu14xz">
              <ReceiptIcon className="w-5 h-5 text-blue-700" data-id="element-1540" data-mp="h0iig8lu7r3u" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1541" data-mp="ls0h2q0e1fln">Total beløb</p>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1542" data-mp="72uj15dadvfc">
            {totalAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1543" data-mp="c2qol91mtux0">
            {invoices.length} fakturaer
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1544" data-mp="3u7twf1izgl14">
          <div className="flex items-center gap-3 mb-2" data-id="element-1545" data-mp="227wjqzwz840">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1546" data-mp="3r5pd8tlqqguq">
              <ReceiptIcon className="w-5 h-5 text-emerald-700" data-id="element-1547" data-mp="3r4xkdqjj2x85" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1548" data-mp="3m7xbl7vangdc">Betalt</p>
          </div>
          <p className="text-3xl font-bold text-emerald-700" data-id="element-1549" data-mp="3heuhmymlu4wm">
            {paidAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1550" data-mp="3cgj86p081wex">
            {invoices.filter(inv => inv.paid).length} fakturaer
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1551" data-mp="33fldwyfl539j">
          <div className="flex items-center gap-3 mb-2" data-id="element-1552" data-mp="2yh1ryy0zrrh6">
            <div className="p-2 bg-red-100 rounded-lg" data-id="element-1553" data-mp="2yhjsw7ylmtvf">
              <ReceiptIcon className="w-5 h-5 text-red-700" data-id="element-1554" data-mp="2tign2lasojme" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1555" data-mp="wpol43fgr9ih">Ubetalt</p>
          </div>
          <p className="text-3xl font-bold text-red-700" data-id="element-1556" data-mp="wnauq7vqalh9">
            {unpaidAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1557" data-mp="10848j8u383ha">
            {invoices.filter(inv => !inv.paid).length} fakturaer
          </p>
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1558" data-mp="18ze9vmbcp3wj">
        <div className="flex items-center gap-3 mb-4" data-id="element-1559" data-mp="la26j32pfq25">
          <FilterIcon className="w-5 h-5 text-stone-600" data-id="element-1560" data-mp="lallcax25yby" />
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-1561" data-mp="q957abbnja4b">
            Søg og filtrer
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1562" data-mp="v9at3av8q2eh">
          <div className="relative" data-id="element-1563" data-mp="21p3bo1vvx7z7">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1564" data-mp="21o1zfu34uffw" />
            <input type="text" placeholder="Søg efter leverandør, faktura nr. eller kunde..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1565" data-mp="26m3joicaoksy" />
          </div>

          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1566" data-mp="y9t6mv43efot">
            <option value="all" data-id="element-1567" data-mp="1361hvxf65oqp">Alle kategorier</option>
            <option value="Sikkerhed" data-id="element-1568" data-mp="fhi8fekdv5x1">Sikkerhed</option>
            <option value="Værktøj" data-id="element-1569" data-mp="ajgoysj1p4l1">Værktøj</option>
            <option value="Materialer" data-id="element-1570" data-mp="ainqeeu8pldz">Materialer</option>
            <option value="Andet" data-id="element-1571" data-mp="2m729phkup7so">Andet</option>
          </select>

          <select value={paidFilter} onChange={e => setPaidFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1572" data-mp="2m4pjkh4xvr4j">
            <option value="all" data-id="element-1573" data-mp="34xa7dk5s761h">Alle status</option>
            <option value="paid" data-id="element-1574" data-mp="34xthiep6wq4y">Betalt</option>
            <option value="unpaid" data-id="element-1575" data-mp="39w4qyobkoymn">Ubetalt</option>
          </select>
        </div>
      </motion.div>

      {/* Invoice List */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-1576" data-mp="2vvk6zotx8wrk">
        {filteredInvoices.length === 0 ? <div className="bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1577" data-mp="1oj20exxhv5ay">
            <ReceiptIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1578" data-mp="1ogp9vwdo7mma" />
            <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1579" data-mp="2g7wmkatlc1xm">
              {searchQuery || categoryFilter !== 'all' || paidFilter !== 'all' ? 'Ingen fakturaer matcher dine filtre' : 'Ingen fakturaer endnu'}
            </h3>
            <p className="text-stone-500 mb-6" data-id="element-1580" data-mp="227lpgq1otr40">
              {searchQuery || categoryFilter !== 'all' || paidFilter !== 'all' ? 'Prøv at justere dine søgekriterier' : isReadOnly ? 'Der er ingen fakturaer i systemet' : 'Tilføj din første faktura for at komme i gang'}
            </p>
            {!searchQuery && categoryFilter === 'all' && paidFilter === 'all' && canAddInvoice && <Button onClick={() => setIsAddModalOpen(true)} data-id="element-1581" data-mp="3ilc1qk2ynloq">
                  Tilføj faktura
                </Button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1582" data-mp="3l5i3cl43r024">
            {filteredInvoices.map((invoice, index) => <InvoiceCard key={invoice.id} invoice={invoice} index={index} onEdit={setEditingInvoice} onDelete={onDeleteInvoice} readOnly={isReadOnly} data-id="element-1583" data-mp="3q5nw64m3ysls" data-mpi={index} />)}
          </div>}
      </motion.div>

      {canAddInvoice && <>
          <AddInvoiceModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={onAddInvoice} customers={customers} data-id="element-1584" data-mp="cy2xo3mosjq7" />

          <EditInvoiceModal isOpen={!!editingInvoice} onClose={() => setEditingInvoice(null)} onSave={onEditInvoice} invoice={editingInvoice} customers={customers} data-id="element-1585" data-mp="mnwa16unl2vi" />
        </>}
    </div>;
}